import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { getBlogPostBySlug, getRelatedPosts } from '../../mocks/blog';
import { useTheme } from '../../hooks/useTheme';
import ArticleContent from './components/ArticleContent';
import ArticleCard from './components/ArticleCard';
import BlogCTA from './components/BlogCTA';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://innoflex.com';

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const post = slug ? getBlogPostBySlug(slug) : undefined;
  const relatedPosts = slug ? getRelatedPosts(slug, 3) : [];

  useEffect(() => {
    if (!post) {
      navigate('/blog', { replace: true });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [post, navigate]);

  // Inject Article Schema.org JSON-LD
  useEffect(() => {
    if (!post) return;
    const schemaId = 'article-schema-jsonld';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${siteUrl}/blog/${post.slug}#article`,
      headline: post.title,
      description: post.excerpt,
      image: {
        '@type': 'ImageObject',
        url: post.coverImage,
        width: 1200,
        height: 630,
      },
      datePublished: post.date,
      dateModified: post.date,
      author: {
        '@type': 'Person',
        name: post.author,
        jobTitle: post.authorRole,
        worksFor: {
          '@id': `${siteUrl}/#organization`,
        },
      },
      publisher: {
        '@type': 'Organization',
        name: 'INNOFLEX',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/221e836cb47596545a3a537d495fa5e5/2baae45286386b606f279993b0bc93de.jpeg',
          width: 200,
          height: 200,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/blog/${post.slug}`,
      },
      keywords: post.tags.join(', '),
      articleSection: post.category,
      inLanguage: 'es',
      isPartOf: {
        '@type': 'Blog',
        '@id': `${siteUrl}/blog#blog`,
        name: 'Blog INNOFLEX — Recursos de Facturación e-CF y DGII',
        url: `${siteUrl}/blog`,
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'INNOFLEX',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: `${siteUrl}/blog`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: post.title,
          item: `${siteUrl}/blog/${post.slug}`,
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = schemaId;
    script.textContent = JSON.stringify([articleSchema, breadcrumbSchema]);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [post]);

  if (!post) return null;

  const canonicalUrl = `${siteUrl}/blog/${post.slug}`;
  const bgStyle = isLight ? { backgroundColor: '#f8fafc' } : { backgroundColor: '#080f1f' };
  const cardBg = isLight ? 'bg-white border-slate-100' : 'bg-white/5 border-white/8';

  return (
    <div style={bgStyle} className="min-h-screen">
      <title>{post.title} | InnoFlex Blog</title>
      <meta name="description" content={post.excerpt} />
      <meta name="keywords" content={post.tags.join(', ')} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="last-modified" content={post.date} />

      {/* Open Graph */}
      <meta property="og:title" content={`${post.title} | InnoFlex Blog`} />
      <meta property="og:description" content={post.excerpt} />
      <meta property="og:image" content={post.coverImage} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content="es_DO" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${post.title} | InnoFlex Blog`} />
      <meta name="twitter:description" content={post.excerpt} />
      <meta name="twitter:image" content={post.coverImage} />

      <Navbar />

      {/* Article Hero */}
      <section className="relative pt-28 pb-0 overflow-hidden">
        {/* Cover image */}
        <div className="relative w-full h-72 sm:h-96 overflow-hidden">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/70" />

          {/* Overlay content */}
          <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-10 max-w-4xl mx-auto left-0 right-0">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Link
                to="/blog"
                className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-body transition-colors cursor-pointer"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-xs" />
                </div>
                Blog
              </Link>
              <i className="ri-arrow-right-s-line text-white/30 text-sm" />
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide uppercase"
                style={{ backgroundColor: post.categoryColor, color: '#fff' }}
              >
                {post.category}
              </span>
            </div>

            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight mb-3">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-navy-950 font-bold text-xs flex-shrink-0"
                  style={{ backgroundColor: '#00cfff' }}
                >
                  IF
                </div>
                <div>
                  <p className="text-xs font-semibold text-white font-body">{post.author}</p>
                  <p className="text-[10px] text-white/50 font-body">{post.authorRole}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-white/50 text-xs font-body">
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-calendar-line text-xs" />
                  </div>
                  {formatDate(post.date)}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-time-line text-xs" />
                  </div>
                  {post.readTime} min lectura
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">

          {/* Main content */}
          <div className="lg:col-span-2">
            {/* Excerpt lead */}
            <p className={`font-body text-base sm:text-lg font-medium leading-relaxed mb-8 pb-8 border-b ${
              isLight ? 'text-navy-700 border-slate-100' : 'text-white/65 border-white/8'
            }`}>
              {post.excerpt}
            </p>

            {/* Content sections */}
            <ArticleContent sections={post.sections} />

            {/* Tags */}
            <div className={`mt-10 pt-6 border-t ${isLight ? 'border-slate-100' : 'border-white/8'}`}>
              <p className={`text-xs font-bold uppercase tracking-widest mb-3 font-body ${isLight ? 'text-slate-400' : 'text-white/30'}`}>
                Etiquetas
              </p>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`px-3 py-1 rounded-full text-xs font-body cursor-default ${
                      isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/8 text-white/50'
                    }`}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Share */}
            <div className={`mt-8 pt-6 border-t flex items-center gap-4 ${isLight ? 'border-slate-100' : 'border-white/8'}`}>
              <p className={`text-xs font-bold uppercase tracking-widest font-body ${isLight ? 'text-slate-400' : 'text-white/30'}`}>
                Compartir
              </p>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(post.title + ' \u2014 ' + canonicalUrl)}`}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold font-body cursor-pointer transition-colors"
                style={{ backgroundColor: '#25D36618', color: '#25D366' }}
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-xs" />
                </div>
                WhatsApp
              </a>
              <button
                onClick={() => { navigator.clipboard.writeText(canonicalUrl); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold font-body cursor-pointer transition-colors ${
                  isLight ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/8 text-white/50 hover:bg-white/15'
                }`}
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-link text-xs" />
                </div>
                Copiar enlace
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1 space-y-6">

            {/* Back to blog */}
            <Link
              to="/blog"
              className={`flex items-center gap-2 text-sm font-semibold font-body cursor-pointer transition-colors hover:text-electric ${
                isLight ? 'text-navy-600' : 'text-white/60'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm" />
              </div>
              Volver al Blog
            </Link>

            {/* Quick CTA */}
            <div
              className={`rounded-2xl p-5 border ${cardBg}`}
              style={{ borderColor: isLight ? 'rgba(0,207,255,0.2)' : 'rgba(0,207,255,0.12)' }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-4" style={{ backgroundColor: '#00cfff18' }}>
                <i className="ri-file-shield-2-line text-electric text-lg" />
              </div>
              <h3 className={`font-heading text-base font-bold mb-2 ${isLight ? 'text-navy-950' : 'text-white'}`}>
                Activa el e-CF en tu negocio
              </h3>
              <p className={`font-body text-xs leading-relaxed mb-4 ${isLight ? 'text-navy-600' : 'text-white/50'}`}>
                Certificación e-CF vía InnoFlex + RepublicaFEL. Pago único desde $300. Lista en 3&#8211;5 días.
              </p>
              <a
                href="https://wa.me/18495388899?text=Hola%20InnoFlex!%20Quiero%20activar%20el%20e-CF%20en%20mi%20negocio."
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-electric to-emerald-400 text-navy-950 text-xs font-extrabold px-4 py-2.5 rounded-full cursor-pointer whitespace-nowrap font-body w-full transition-all hover:scale-105"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-xs" />
                </div>
                Consultar por WhatsApp
              </a>
            </div>

            {/* Table of contents */}
            <div className={`rounded-2xl p-5 border ${cardBg}`}>
              <h3 className={`font-heading text-sm font-bold mb-4 ${isLight ? 'text-navy-950' : 'text-white'}`}>
                En este artículo
              </h3>
              <nav className="space-y-2">
                {post.sections
                  .filter((s) => s.type === 'heading2')
                  .map((s, idx) => (
                    <p key={idx} className={`text-xs font-body leading-snug py-1 border-l-2 pl-3 ${
                      isLight ? 'text-navy-600 border-slate-200' : 'text-white/55 border-white/10'
                    }`}>
                      {s.content}
                    </p>
                  ))}
              </nav>
            </div>

            {/* Key facts */}
            <div className={`rounded-2xl p-5 border ${cardBg}`}>
              <h3 className={`font-heading text-sm font-bold mb-4 ${isLight ? 'text-navy-950' : 'text-white'}`}>
                Datos clave
              </h3>
              <div className="space-y-3">
                {[
                  { icon: 'ri-time-line', label: 'Activación e-CF', value: '3&#8211;5 días hábiles' },
                  { icon: 'ri-price-tag-3-line', label: 'Costo Certificación', value: 'Desde $300 (único)' },
                  { icon: 'ri-building-4-line', label: 'Proveedor certificado', value: 'RepublicaFEL + DGII' },
                  { icon: 'ri-customer-service-2-line', label: 'Soporte', value: 'WhatsApp 24/7' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                      style={{ backgroundColor: '#00cfff15', color: '#00cfff' }}
                    >
                      <i className={`${item.icon} text-sm`} />
                    </div>
                    <div>
                      <p className={`text-[10px] font-body ${isLight ? 'text-slate-400' : 'text-white/35'}`}>{item.label}</p>
                      <p
                        className={`text-xs font-semibold font-body ${isLight ? 'text-navy-800' : 'text-white/80'}`}
                        dangerouslySetInnerHTML={{ __html: item.value }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* Related articles */}
        {relatedPosts.length > 0 && (
          <div className={`mt-16 pt-10 border-t ${isLight ? 'border-slate-100' : 'border-white/8'}`}>
            <div className="flex items-center gap-3 mb-8">
              <h2 className={`font-heading text-xl font-bold ${isLight ? 'text-navy-950' : 'text-white'}`}>
                Artículos relacionados
              </h2>
              <div className="flex-1 h-px" style={{ backgroundColor: isLight ? 'rgba(10,22,40,0.06)' : 'rgba(255,255,255,0.06)' }} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map((p) => (
                <ArticleCard key={p.slug} post={p} />
              ))}
            </div>
          </div>
        )}
      </main>

      <BlogCTA />
      <Footer />
    </div>
  );
}
